/**
 * 并发控制和重试工具
 * 用于限制并发请求数量和处理 API 调用失败重试
 */

export interface Semaphore {
  acquire: (timeout?: number) => Promise<void>
  release: () => void
  getCurrent: () => number
}

export interface RetryOptions {
  timeout?: number
  retries?: number
  retryDelay?: number
  onRetry?: (attempt: number, error: Error) => void
}

/**
 * 创建并发控制信号量
 * @param max 最大并发数
 * @returns 信号量对象
 */
export function createSemaphore(max: number): Semaphore {
  let current = 0
  const queue: Array<{ resolve: () => void; timer?: any }> = []

  const acquire = (timeout?: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (current < max) {
        current++
        resolve()
        return
      }

      // 添加到等待队列
      const item: { resolve: () => void; timer?: any } = { resolve }

      // 如果设置了超时，添加超时处理
      if (timeout && timeout > 0) {
        item.timer = setTimeout(() => {
          const index = queue.indexOf(item)
          if (index > -1) {
            queue.splice(index, 1)
            reject(new Error(`信号量获取超时 (${timeout}ms)`))
          }
        }, timeout)
      }

      queue.push(item)
    })
  }

  const release = () => {
    if (current <= 0) {
      console.warn('[Semaphore] release 调用异常：current 已为 0')
      return
    }
    current--
    if (queue.length > 0) {
      current++
      const next = queue.shift()!
      if (next.timer) {
        clearTimeout(next.timer)
      }
      next.resolve()
    }
  }

  const getCurrent = () => current

  return { acquire, release, getCurrent }
}

/**
 * 带超时和重试的 API 调用
 * @param fn 要执行的异步函数
 * @param options 重试选项
 * @returns Promise
 */
export async function callWithRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    timeout = 120000,
    retries = 3,
    retryDelay = 1000,
    onRetry
  } = options

  for (let attempt = 1; attempt <= retries; attempt++) {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    
    try {
      // 创建超时 Promise（并正确清理定时器）
      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`请求超时 (${timeout}ms)`)),
          timeout
        )
      })

      // 竞速：实际请求 vs 超时
      const result = await Promise.race([fn(), timeoutPromise])
      
      // 请求成功，清除超时定时器
      if (timeoutId) clearTimeout(timeoutId)
      
      return result
    } catch (error) {
      // 清除超时定时器
      if (timeoutId) clearTimeout(timeoutId)
      
      const err = error instanceof Error ? error : new Error(String(error))

      // 如果是最后一次尝试，直接抛出错误
      if (attempt === retries) {
        throw err
      }

      // 通知调用方正在重试
      onRetry?.(attempt, err)

      // 指数退避：等待时间递增
      await new Promise(resolve =>
        setTimeout(resolve, retryDelay * Math.pow(2, attempt - 1))
      )
    }
  }

  throw new Error('重试失败')
}
